import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, BookOpen, CheckCircle, AlertTriangle, Info,
    Activity, Scale, Stethoscope, FileText, ExternalLink
} from 'lucide-react';

const LOGO_URL = '/logo.png';

const GUIDELINES = {
    hip_knee: {
        title: "Hip & Knee Arthroplasty",
        bodies: ["BOA", "NICE (NG226)", "RCSEng"],
        sections: [
            {
                title: "BMI Requirements",
                icon: Scale,
                criteria: [
                    { text: "BMI < 40 kg/m² for elective joint replacement", type: "must" },
                    { text: "BMI 35-40: Consider with MDT discussion and optimization", type: "consider" },
                    { text: "BMI > 40: Mandatory weight management program first", type: "warning" }
                ],
                source: "NICE NG226, BOA Guidelines 2023"
            },
            {
                title: "Conservative Treatment",
                icon: Activity,
                criteria: [
                    { text: "Minimum 6 weeks of physiotherapy", type: "must" },
                    { text: "Trial of regular analgesia (paracetamol, NSAIDs)", type: "must" },
                    { text: "Weight management advice and support", type: "must" },
                    { text: "Activity modification and aids (walking stick, shoe modification)", type: "should" },
                    { text: "Consider intra-articular injections if appropriate", type: "consider" }
                ],
                source: "NICE NG226"
            },
            {
                title: "Clinical Indicators",
                icon: Stethoscope,
                criteria: [
                    { text: "Oxford Hip/Knee Score ≤ 39/48", type: "must" },
                    { text: "Significant functional limitation affecting daily activities", type: "must" },
                    { text: "Rest pain or night pain", type: "should" },
                    { text: "Radiographic evidence of osteoarthritis", type: "must" },
                    { text: "Symptoms for at least 3 months", type: "must" }
                ],
                source: "BOA Primary Hip Replacement Guidelines"
            },
            {
                title: "Diabetes Control",
                icon: Activity,
                criteria: [
                    { text: "HbA1c < 69 mmol/mol (8.5%) for elective surgery", type: "must" },
                    { text: "HbA1c 69-86: Optimize before surgery", type: "warning" },
                    { text: "HbA1c > 86: Defer until control improved", type: "warning" }
                ],
                source: "Getting It Right First Time (GIRFT)"
            }
        ]
    },
    shoulder: {
        title: "Shoulder Conditions",
        bodies: ["BESS", "BOA", "NICE"],
        sections: [
            {
                title: "Rotator Cuff Repair Criteria",
                icon: Stethoscope,
                criteria: [
                    { text: "Failure of 6-12 weeks supervised physiotherapy", type: "must" },
                    { text: "MRI or ultrasound confirmed tear", type: "must" },
                    { text: "ASES Score < 70", type: "should" },
                    { text: "Consider patient age and activity level", type: "consider" },
                    { text: "Acute traumatic tears: Consider early referral", type: "consider" }
                ],
                source: "BESS Guidelines 2024"
            },
            {
                title: "Shoulder Arthroplasty",
                icon: Activity,
                criteria: [
                    { text: "Radiographic evidence of glenohumeral arthritis", type: "must" },
                    { text: "Failed conservative management minimum 3 months", type: "must" },
                    { text: "Significant functional impairment", type: "must" },
                    { text: "Pain affecting sleep and daily activities", type: "should" }
                ],
                source: "BESS, NICE"
            }
        ]
    },
    spine: {
        title: "Spinal Conditions",
        bodies: ["BASS", "NICE", "RCSEng"],
        sections: [
            {
                title: "Red Flags - Urgent Referral Required",
                icon: AlertTriangle,
                criteria: [
                    { text: "Cauda equina syndrome symptoms", type: "urgent" },
                    { text: "Progressive neurological deficit", type: "urgent" },
                    { text: "Suspected spinal malignancy or infection", type: "urgent" },
                    { text: "Severe or worsening pain despite treatment", type: "warning" }
                ],
                source: "NICE NG59"
            },
            {
                title: "Lumbar Spinal Stenosis",
                icon: Stethoscope,
                criteria: [
                    { text: "Neurogenic claudication symptoms", type: "must" },
                    { text: "MRI confirmation of spinal stenosis", type: "must" },
                    { text: "Failure of 6 weeks physiotherapy", type: "must" },
                    { text: "ODI Score > 40%", type: "should" },
                    { text: "Trial of epidural injection considered", type: "consider" }
                ],
                source: "NICE NG59, BASS"
            },
            {
                title: "Lumbar Disc Herniation",
                icon: Activity,
                criteria: [
                    { text: "Concordant radicular symptoms > 6 weeks", type: "must" },
                    { text: "Failure of conservative management", type: "must" },
                    { text: "MRI confirmed disc pathology", type: "must" },
                    { text: "Consider nerve root injection first", type: "consider" }
                ],
                source: "NICE NG59"
            }
        ]
    },
    foot_ankle: {
        title: "Foot & Ankle",
        bodies: ["BOFAS", "NICE"],
        sections: [
            {
                title: "Hallux Valgus Surgery",
                icon: Stethoscope,
                criteria: [
                    { text: "Pain and disability from bunion deformity", type: "must" },
                    { text: "Failed footwear modification for 3 months", type: "must" },
                    { text: "AOFAS Score indicating surgical intervention", type: "should" },
                    { text: "Cosmetic concerns alone are NOT an indication", type: "warning" }
                ],
                source: "BOFAS Guidelines"
            },
            {
                title: "Ankle Arthroplasty/Fusion",
                icon: Activity,
                criteria: [
                    { text: "End-stage ankle arthritis", type: "must" },
                    { text: "Significant functional limitation", type: "must" },
                    { text: "Failed conservative measures minimum 6 months", type: "must" },
                    { text: "Patient education regarding options (fusion vs replacement)", type: "should" }
                ],
                source: "BOFAS, NICE"
            }
        ]
    },
    hand_wrist: {
        title: "Hand & Wrist",
        bodies: ["BSSH", "BESS", "NICE"],
        sections: [
            {
                title: "Carpal Tunnel Syndrome",
                icon: Stethoscope,
                criteria: [
                    { text: "Clinical diagnosis of CTS (Phalen's, Tinel's positive)", type: "must" },
                    { text: "Nerve conduction studies if diagnosis uncertain", type: "consider" },
                    { text: "Trial of night splinting for 6 weeks", type: "should" },
                    { text: "Steroid injection trial before surgery", type: "should" },
                    { text: "Thenar wasting: Consider early surgery", type: "warning" }
                ],
                source: "BSSH Guidelines, NICE CG173"
            },
            {
                title: "Trigger Finger",
                icon: Activity,
                criteria: [
                    { text: "Painful triggering affecting function", type: "must" },
                    { text: "Trial of steroid injection (up to 2)", type: "should" },
                    { text: "Diabetic patients: May have reduced injection response", type: "warning" }
                ],
                source: "BSSH"
            }
        ]
    }
};

export default function GuidelinesPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('hip_knee');

    const getCriteriaStyle = (type) => {
        switch (type) {
            case 'must':
                return 'bg-teal-50 border-teal-200 text-teal-800';
            case 'should':
                return 'bg-blue-50 border-blue-200 text-blue-800';
            case 'consider':
                return 'bg-slate-50 border-slate-200 text-slate-700';
            case 'warning':
                return 'bg-amber-50 border-amber-200 text-amber-800';
            case 'urgent':
                return 'bg-red-50 border-red-200 text-red-800';
            default:
                return 'bg-slate-50 border-slate-200 text-slate-700';
        }
    };

    const getCriteriaIcon = (type) => {
        switch (type) {
            case 'must':
                return <CheckCircle className="w-4 h-4 text-teal-600" />;
            case 'warning':
            case 'urgent':
                return <AlertTriangle className="w-4 h-4 text-amber-600" />;
            default:
                return <Info className="w-4 h-4 text-blue-600" />;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="guidelines-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Dashboard
                        </Button>
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-teal-600" />
                            <span className="font-semibold text-slate-900">Referral Guidelines</span>
                        </div>
                        <div className="w-20"></div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Introduction */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <Card className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                        <CardContent className="p-6">
                            <h1 className="text-2xl font-bold mb-2">NHS Orthopaedic Referral Guidelines</h1>
                            <p className="text-teal-100 mb-4">
                                Evidence-based criteria from BOA, NICE, BESS, BOFAS, BSSH, BASS, and RCSEng to ensure 
                                appropriate referrals and optimal patient outcomes.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <Badge className="bg-white/20 text-white">BOA</Badge>
                                <Badge className="bg-white/20 text-white">NICE</Badge>
                                <Badge className="bg-white/20 text-white">BESS</Badge>
                                <Badge className="bg-white/20 text-white">BOFAS</Badge>
                                <Badge className="bg-white/20 text-white">BSSH</Badge>
                                <Badge className="bg-white/20 text-white">BASS</Badge>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Legend */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-6"
                >
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-wrap gap-4 text-sm">
                                <span className="flex items-center gap-2">
                                    <span className="px-2 py-1 bg-teal-50 border border-teal-200 rounded text-teal-700">Must</span>
                                    Mandatory requirement
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-blue-700">Should</span>
                                    Strongly recommended
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-slate-700">Consider</span>
                                    May be appropriate
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-700">Warning</span>
                                    Requires attention
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="px-2 py-1 bg-red-50 border border-red-200 rounded text-red-700">Urgent</span>
                                    Immediate action
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Guidelines Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-5 mb-6">
                        <TabsTrigger value="hip_knee">Hip & Knee</TabsTrigger>
                        <TabsTrigger value="shoulder">Shoulder</TabsTrigger>
                        <TabsTrigger value="spine">Spine</TabsTrigger>
                        <TabsTrigger value="foot_ankle">Foot & Ankle</TabsTrigger>
                        <TabsTrigger value="hand_wrist">Hand & Wrist</TabsTrigger>
                    </TabsList>

                    {Object.entries(GUIDELINES).map(([key, guideline]) => (
                        <TabsContent key={key} value={key}>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <h2 className="text-xl font-bold text-slate-900">{guideline.title}</h2>
                                    <div className="flex gap-2">
                                        {guideline.bodies.map(body => (
                                            <Badge key={body} variant="secondary">{body}</Badge>
                                        ))}
                                    </div>
                                </div>

                                {guideline.sections.map((section, index) => (
                                    <Card key={index}>
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2 text-lg">
                                                <section.icon className="w-5 h-5 text-teal-600" />
                                                {section.title}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {section.criteria.map((criterion, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`flex items-start gap-3 p-3 rounded-lg border ${getCriteriaStyle(criterion.type)}`}
                                                    >
                                                        {getCriteriaIcon(criterion.type)}
                                                        <span className="text-sm flex-1">{criterion.text}</span>
                                                        <Badge variant="outline" className="text-xs">
                                                            {criterion.type}
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-4 flex items-center gap-1">
                                                <FileText className="w-3 h-3" />
                                                Source: {section.source}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </motion.div>
                        </TabsContent>
                    ))}
                </Tabs>

                {/* External Resources */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>External Resources</CardTitle>
                            <CardDescription>Official guideline documents and references</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[
                                    { name: "NICE NG226", desc: "Osteoarthritis in Adults", url: "https://www.nice.org.uk/guidance/ng226" },
                                    { name: "BOA Guidelines", desc: "Primary Hip Replacement", url: "https://www.boa.ac.uk" },
                                    { name: "NICE NG59", desc: "Low Back Pain & Sciatica", url: "https://www.nice.org.uk/guidance/ng59" },
                                    { name: "BESS Guidelines", desc: "Shoulder Conditions", url: "https://www.bess.ac.uk" },
                                    { name: "BOFAS Standards", desc: "Foot & Ankle Surgery", url: "https://www.bofas.org.uk" },
                                    { name: "BSSH Guidelines", desc: "Hand Surgery", url: "https://www.bssh.ac.uk" }
                                ].map(resource => (
                                    <a
                                        key={resource.name}
                                        href={resource.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    >
                                        <div>
                                            <p className="font-medium text-slate-900">{resource.name}</p>
                                            <p className="text-sm text-slate-500">{resource.desc}</p>
                                        </div>
                                        <ExternalLink className="w-4 h-4 text-slate-400" />
                                    </a>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
}
